import _ from 'underscore'
import React from 'react'
import PropTypes from 'prop-types'

import Form from '../elements/Form'
import TextInput from '../elements/forms/TextInput'
import StaticInput from '../elements/forms/StaticInput'
import CheckboxInput from '../elements/forms/CheckboxInput'
import ColorSelect from '../elements/forms/ColorSelect'
import LocationSelect from '../elements/forms/LocationSelect'
import Sticker from '../elements/Sticker'
import Dismissible from '../elements/Dismissible'
import AppConstants from '../../utils/AppConstants'
import AppUtils from '../../utils/AppUtils'

const ERROR = {
  EMPTY_NAME: 'Le nom ne peut pas être vide.',
  EMPTY_COLOR: 'Veuillez sélectionner une couleur.'
}

class LocationForm extends React.Component {

  constructor(props) {

    // Properties
    super(props)

    // State
    this.state = {
      input: this.getInitialInput(props),
      formErrors: []
    }

    // Bind events
    this.onSubmit = this.onSubmit.bind(this)
    this.onChangeInputParent = this.onChangeInputParent.bind(this)
    this.onChangeInputName = this.onChangeInputName.bind(this)
    this.onChangeInputColor = this.onChangeInputColor.bind(this)
    this.onChangeCheckboxLabeled = this.onChangeCheckboxLabeled.bind(this)
    this.onChangeCheckboxInspect = this.onChangeCheckboxInspect.bind(this)
    this.onChangeInputRemark = this.onChangeInputRemark.bind(this)

    // Register submit event
    if (_.isFunction(this.props.registerSubmit)) {
      this.props.registerSubmit(this.onSubmit)
    }
  }

  getInitialInput(props = this.props) {
    return _.extend(
      {
        name: (props.data && props.data.name) || '',
        color: (props.data && props.data.color && props.data.color.id) || null,
        islabeled: (props.data && props.data.islabeled) || false,
        pipeinspect: (props.data && props.data.pipeinspect) || false,
        remark: (props.data && props.data.remark) || ''
      },
      props.data
        ? {}
        : {
            parent: -1
          }
    )
  }

  setInput(input = {}) {
    this.setState((prevState, props) => ({
      input: _.extend(
        {},
        prevState.input,
        input
      )
    }))
  }

  componentDidUpdate(prevProps, prevState) {
    // When async fetch finished
    if (prevProps.isLoading && !this.props.isLoading) {
      this.setState({
        input: this.getInitialInput()
      })
    }
  }

  onChangeInputParent(parent) {
    this.setInput({ parent })
  }

  onChangeInputName(name) {
    this.setInput({ name })
  }

  onChangeInputColor(color) {
    this.setInput({ color })
  }

  onChangeCheckboxLabeled(islabeled) {
    this.setInput({ islabeled })
  }

  onChangeCheckboxInspect(pipeinspect) {
    this.setInput({ pipeinspect })
  }

  onChangeInputRemark(remark) {
    this.setInput({ remark })
  }

  onSubmit() {
    // Check that fields have been filled
    const formErrors = []
    if (!_.isString(this.state.input.name) || this.state.input.name.length === 0) {
      formErrors.push(ERROR.EMPTY_NAME)
    }
    if (_.isNull(this.state.input.color)) {
      formErrors.push(ERROR.EMPTY_COLOR)
    }
    this.setState({
      formErrors
    })
    if (!_.isEmpty(formErrors)) {
      return;
    }

    // Build and submit correctly formatted input
    const input = _.extend(
      {},
      _.pick(this.state.input, 'name', 'islabeled', 'pipeinspect', 'remark'),
      {
        color: {
          id: this.state.input.color
        }
      },
      _.isUndefined(this.state.input.parent)
        ? {}
        : {
            parent: this.state.input.parent === -1
              ? null
              : {
                  id: this.state.input.parent
                }
          }
    )
    this.props.onSubmit(input)
  }

  render() {
    return (
      <React.Fragment>
        {
          this.props.error
            ? <Dismissible
                header={AppUtils.errorToTitle(this.props.error)}
                content={AppUtils.errorToText(this.props.error, {
                  [AppConstants.ERROR.BAD_REQUEST.UNICITY_CONSTRAINT_VIOLATION]: 'Un emplacement avec ce nom existe déjà.'
                })}
                color='red'
                onDismiss={this.props.onClearError}
              />
            : null
        }
        {
          !_.isEmpty(this.state.formErrors)
            ? <Dismissible
                content='Veuillez résoudre les problèmes ci-dessous avant de poursuivre.'
                color='orange'
              />
            : null
        }
        <Form
          onSubmit={this.onSubmit}
          loading={this.props.isLoading || this.props.isSaving}
        >
          {
            this.props.data
              ? <StaticInput
                  label='Parent'
                  content={
                    _.isNull(this.props.data.parent)
                      ? 'Aucun'
                      : <Sticker
                          foreground={this.props.data.parent.color.foreground}
                          background={this.props.data.parent.color.background}
                          text={this.props.data.parent.name}
                        />
                  }
                />
              : <LocationSelect
                  label='Parent'
                  loading={this.props.locationsIsFetching}
                  options={_.union(
                    [{
                      id: -1,
                      stickers: [{
                        name: 'Aucun',
                        color: {
                          foreground: '000000',
                          background: 'ffffff'
                        }
                      }]
                    }],
                    this.props.locationsList
                  )}
                  selected={this.state.input.parent}
                  onChange={this.onChangeInputParent}
                />
          }
          <TextInput
            label='Nom'
            placeholder='Village/Véhicule'
            value={this.state.input.name}
            onChange={this.onChangeInputName}
            error={
              _.contains(this.state.formErrors, ERROR.EMPTY_NAME)
                ? ERROR.EMPTY_NAME
                : null
            }
          />
          <ColorSelect
            label='Couleur'
            loading={this.props.colorsIsFetching}
            options={this.props.colorsList}
            selected={this.state.input.color}
            onChange={this.onChangeInputColor}
            error={
              _.contains(this.state.formErrors, ERROR.EMPTY_COLOR)
                ? ERROR.EMPTY_COLOR
                : null
            }
          />
          <CheckboxInput
            label='Etiquettage'
            text='Etiquetté'
            checked={this.state.input.islabeled}
            onChange={this.onChangeCheckboxLabeled}
          />
          <CheckboxInput
            label='Inspection'
            text='Tuyaux'
            checked={this.state.input.pipeinspect}
            onChange={this.onChangeCheckboxInspect}
          />
          <TextInput
            label='Remarque'
            placeholder='...'
            value={this.state.input.remark}
            onChange={this.onChangeInputRemark}
            multiline={true}
          />
        </Form>
      </React.Fragment>
    )
  }
}

LocationForm.propTypes = {
  data: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClearError: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  registerSubmit: PropTypes.func,
  error: PropTypes.object,
  locationsList: PropTypes.array,
  locationsIsFetching: PropTypes.bool.isRequired,
  colorsList: PropTypes.array,
  colorsIsFetching: PropTypes.bool.isRequired
}

export default LocationForm



// WEBPACK FOOTER //
// ./src/components/forms/LocationForm.js